import { useCallback, useEffect, useRef, useState } from "react";
import type { DeveloperCreditPayload } from "../../api/admin-revenue";
import { t } from "../../i18n";
import { developerCreditStyles as styles } from "./developer-credit-styles";

type DeveloperCreditForm = {
  readonly amount_minor: string;
  readonly currency: string;
  readonly reason: string;
};

type DeveloperCreditField = keyof DeveloperCreditForm;

const EMPTY_FORM: DeveloperCreditForm = {
  amount_minor: "",
  currency: "USD",
  reason: "",
};

const MAX_DEVELOPER_CREDIT_MINOR = 10_000_000;

export function DeveloperCreditDialog({
  open,
  developerUserId,
  onConfirm,
  onCancel,
}: {
  readonly open: boolean;
  readonly developerUserId: string;
  readonly onConfirm: (payload: DeveloperCreditPayload) => void;
  readonly onCancel: () => void;
}): React.JSX.Element {
  const [form, setForm] = useState<DeveloperCreditForm>(EMPTY_FORM);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const amountMinor = Number(form.amount_minor);
  const canConfirm = isValidDeveloperCredit(form, amountMinor);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  const close = useCallback((): void => {
    setForm(EMPTY_FORM);
    onCancel();
  }, [onCancel]);

  const submit = useCallback((): void => {
    if (!canConfirm) return;
    onConfirm({
      amount_minor: amountMinor,
      currency: form.currency.trim().toUpperCase(),
      reason: form.reason.trim(),
    });
    setForm(EMPTY_FORM);
  }, [amountMinor, canConfirm, form, onConfirm]);

  const updateField = useCallback(
    (field: DeveloperCreditField, value: string): void => {
      setForm((current) => ({ ...current, [field]: value }));
    },
    [],
  );

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="developer-credit-title"
      style={styles.dialog}
      onCancel={(event) => {
        event.preventDefault();
        close();
      }}
    >
      <DeveloperCreditContent
        developerUserId={developerUserId}
        form={form}
        canConfirm={canConfirm}
        onFieldChange={updateField}
        onCancel={close}
        onSubmit={submit}
      />
    </dialog>
  );
}

function DeveloperCreditContent({
  developerUserId,
  form,
  canConfirm,
  onFieldChange,
  onCancel,
  onSubmit,
}: {
  readonly developerUserId: string;
  readonly form: DeveloperCreditForm;
  readonly canConfirm: boolean;
  readonly onFieldChange: (field: DeveloperCreditField, value: string) => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}): React.JSX.Element {
  return (
    <div style={styles.content}>
      <h2 id="developer-credit-title" style={styles.title}>
        {t("billing.developer_credit_title")}
      </h2>
      <p style={styles.description}>
        {t("billing.developer_credit_desc", { userId: developerUserId })}
      </p>
      <DeveloperCreditFields form={form} onFieldChange={onFieldChange} />
      <DeveloperCreditActions
        canConfirm={canConfirm}
        onCancel={onCancel}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function DeveloperCreditFields({
  form,
  onFieldChange,
}: {
  readonly form: DeveloperCreditForm;
  readonly onFieldChange: (field: DeveloperCreditField, value: string) => void;
}): React.JSX.Element {
  return (
    <div style={styles.grid}>
      <CreditInput
        field="amount_minor"
        labelKey="billing.developer_credit_amount_minor"
        type="number"
        value={form.amount_minor}
        onFieldChange={onFieldChange}
      />
      <CreditInput
        field="currency"
        labelKey="billing.developer_credit_currency"
        type="text"
        value={form.currency}
        onFieldChange={onFieldChange}
      />
      <CreditReason value={form.reason} onFieldChange={onFieldChange} />
    </div>
  );
}

function CreditInput({
  field,
  labelKey,
  type,
  value,
  onFieldChange,
}: {
  readonly field: "amount_minor" | "currency";
  readonly labelKey: string;
  readonly type: string;
  readonly value: string;
  readonly onFieldChange: (field: DeveloperCreditField, value: string) => void;
}): React.JSX.Element {
  const id = `developer-credit-${field}`;
  return (
    <label htmlFor={id} style={styles.field}>
      <span style={styles.label}>{t(labelKey)}</span>
      <input
        id={id}
        type={type}
        min={field === "amount_minor" ? 1 : undefined}
        max={field === "amount_minor" ? MAX_DEVELOPER_CREDIT_MINOR : undefined}
        value={value}
        onChange={(event) => onFieldChange(field, event.target.value)}
        style={styles.input}
      />
    </label>
  );
}

function CreditReason({
  value,
  onFieldChange,
}: {
  readonly value: string;
  readonly onFieldChange: (field: DeveloperCreditField, value: string) => void;
}): React.JSX.Element {
  return (
    <label htmlFor="developer-credit-reason" style={styles.fieldFull}>
      <span style={styles.label}>{t("billing.developer_credit_reason")}</span>
      <textarea
        id="developer-credit-reason"
        rows={3}
        value={value}
        onChange={(event) => onFieldChange("reason", event.target.value)}
        style={styles.textarea}
      />
    </label>
  );
}

function DeveloperCreditActions({
  canConfirm,
  onCancel,
  onSubmit,
}: {
  readonly canConfirm: boolean;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
}): React.JSX.Element {
  return (
    <div style={styles.actions}>
      <button type="button" onClick={onCancel} style={styles.cancelBtn}>
        {t("common.cancel")}
      </button>
      <button
        type="button"
        disabled={!canConfirm}
        onClick={onSubmit}
        style={{
          ...styles.confirmBtn,
          ...(canConfirm ? undefined : styles.confirmBtnDisabled),
        }}
      >
        {t("billing.developer_credit_confirm")}
      </button>
    </div>
  );
}

function isValidDeveloperCredit(
  form: DeveloperCreditForm,
  amountMinor: number,
): boolean {
  return (
    Number.isSafeInteger(amountMinor) &&
    amountMinor > 0 &&
    amountMinor <= MAX_DEVELOPER_CREDIT_MINOR &&
    form.currency.trim().length > 0 &&
    form.reason.trim().length >= 10
  );
}
